/**
 * Repairs an API-shaped MATH document so the editor holds strings where the schema describes them.
 *
 * Laravel's `ConvertEmptyStringsToNull` middleware rewrites every `''` in the request — including
 * inside `data` — so a field the author has not filled in yet comes back as `null` on the first
 * save. Left alone, each one reports Zod's raw "expected string, received null" and the editors
 * bind `null` into their inputs. The inverse is exact: a required string that is `null` was `''`.
 * Optional strings (`hint`, `explanation`, `description`) are dropped instead, which is what the
 * store writes for an emptied optional field.
 *
 * It lives beside `math-practice-schema.ts` because the list of string fields IS schema knowledge
 * and must change in the same edit. Non-mutating and idempotent; unknown keys pass through.
 */
const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** Required string fields on an exercise itself. */
const EXERCISE_STRING_KEYS: readonly string[] = ['answer', 'correctOptionId', 'correctTickId']

/** Optional string fields, dropped when `null`. */
const OPTIONAL_KEYS: readonly string[] = ['hint', 'explanation']

/** Each list an exercise type may carry, with the string fields of its items. */
const LIST_STRING_KEYS: Readonly<Record<string, readonly string[]>> = {
  options: ['text'],
  pairs: ['left', 'right'],
  items: ['text'],
  ticks: ['label'],
  steps: ['label', 'answer'],
}

const withStrings = (record: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> => {
  const repaired = { ...record }
  keys.forEach(key => {
    if (repaired[key] === null) {
      repaired[key] = ''
    }
  })

  return repaired
}

const withoutNulls = (record: Record<string, unknown>, keys: readonly string[]): Record<string, unknown> =>
  Object.fromEntries(Object.entries(record).filter(([key, value]) => !(value === null && keys.includes(key))))

const repairPromptBlock = (block: unknown): unknown => {
  if (!isRecord(block)) {
    return block
  }
  if (block.type === 'text') {
    return withStrings(block, ['value'])
  }
  if (block.type === 'image') {
    const repaired = withStrings(block, ['alt'])

    return isRecord(repaired.asset) ? { ...repaired, asset: withStrings(repaired.asset, ['url']) } : repaired
  }

  return block
}

const repairList = (items: unknown, keys: readonly string[]): unknown =>
  Array.isArray(items) ? items.map(item => (isRecord(item) ? withStrings(item, keys) : item)) : items

const repairExercise = (exercise: unknown): unknown => {
  if (!isRecord(exercise)) {
    return exercise
  }
  const repaired = withoutNulls(withStrings(exercise, EXERCISE_STRING_KEYS), OPTIONAL_KEYS)
  if (Array.isArray(repaired.prompt)) {
    repaired.prompt = repaired.prompt.map(repairPromptBlock)
  }
  Object.entries(LIST_STRING_KEYS).forEach(([list, keys]) => {
    if (list in repaired) {
      repaired[list] = repairList(repaired[list], keys)
    }
  })

  return repaired
}

export const normalizeMathPracticeDocument = (raw: unknown): unknown => {
  if (!isRecord(raw) || !isRecord(raw.definition)) {
    return raw
  }
  const definition = withoutNulls(withStrings(raw.definition, ['title']), ['description'])
  if (Array.isArray(definition.exercises)) {
    definition.exercises = definition.exercises.map(repairExercise)
  }

  return { ...raw, definition }
}
